<template>
  <div class="card"
       id="detail-general">
    <header class="header-card">
      <p>
        {{ $t('detail.generalInfos.title')}}
      </p>
    </header>
    <div class="card-content">
      <div class="columns is-multiline">
        <div class="column is-3">
          <div class="is-italic">{{ getTitleDateDevisSignature }}</div>
          <div class="has-text-weight-bold">{{ info.dateDevisCommande }}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ labelEstSigne }}</div>
          <div class="has-text-weight-bold">
            {{ $t(`general.${info.estSigne ? 'oui' : 'non'}`) }}
          </div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.generalInfos.dateValidite') }}</div>
          <div class="has-text-weight-bold">{{ info.dateValidite }}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.generalInfos.datePremierAffichage') }}</div>
          <div class="has-text-weight-bold">{{ info.datePremierAffichage }}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.generalInfos.categorieVente') }}</div>
          <div class="has-text-weight-bold">{{ $t(info.categorieVente) }}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.generalInfos.commercial') }}</div>
          <div class="has-text-weight-bold"  v-if="info.commercial">
            {{ info.commercial.nom }} {{info.commercial.prenom}}
          </div>
        </div>
        <div class="column is-narrow" v-if="afficherCommission">
          <div class="is-italic">{{ $t('detail.commande.commission') }}</div>
          <strong v-if="info.commission">{{ info.commission }} %</strong>
        </div>
        <div class="column" :class="afficherCommission ? 'is-2' : 'is-3'">
          <div class="is-italic">{{ $t('detail.generalInfos.assistante') }}</div>
          <div class="has-text-weight-bold" v-if="info.assistant">
            {{ info.assistant.nom }} {{info.assistant.prenom}}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.generalInfos.agence') }}</div>
          <div class="has-text-weight-bold">{{ getAgenceLibelle }}</div>
        </div>
        <div class="column is-3" v-if="societeActive.roles.length > 1">
          <div class="is-italic">{{ $t('detail.generalInfos.role') }}</div>
          <div class="has-text-weight-bold">{{ $t(getRole) }}</div>
        </div>
        <div class="column is-3"
             v-if="info.typeReferenceExterne === constantes.TYPE_REFERENCE_EXTERNE_ACHAT">
          <div class="is-italic">{{ $t('detail.generalInfos.commandeAchatInterne') }}</div>
          <router-link-blank :message="info.referenceClient"
                             :to="{ name: 'c.bodac.commandes-achatVenteInterne',
                                      params: { id: info.referenceExterne } }"
                             class-message="lien">
          </router-link-blank>
        </div>
        <div class="column is-3"
             v-else-if=
               "info.typeReferenceExterne === constantes.TYPE_REFERENCE_EXTERNE_VENTE">
          <div class="is-italic">{{ $t('detail.generalInfos.commandeVenteInterne') }}</div>
          <router-link-blank :message="info.referenceClient"
                             :to="{ name: 'c.bodac.commandes',
                                      params: { id: info.referenceExterne } }"
                             class-message="lien">
          </router-link-blank>
        </div>
        <div class="column is-3"
             v-else>
          <div class="is-italic">{{ $t('detail.commande.commercial.reference') }}</div>
          <div class="has-text-weight-bold">{{ info.referenceClient }}</div>
        </div>
        <div class="column is-3">
          <div class="is-italic">{{ $t('detail.commande.folderCode') }}</div>
          <div class="has-text-weight-bold">{{ info.codeDossier }}</div>
        </div>
        <div class="column is-12 columns is-marginless is-paddingless">
          <div class="column is-3" v-if="moduleCampagneActive">
            <div class="is-italic">{{ $t('detail.generalInfos.campagne') }}</div>
            <strong>
              <router-link-blank v-if="info.campagneId"
                :message="info.campagneLibelle"
                :to="{
                  name: 'c.bodac.campagne',
                  params: { id: info.campagneId },
                }"
                class-message="lien">
              </router-link-blank>
            </strong>
          </div>
          <div class="column is-6">
            <div class="is-italic">{{ $t('detail.generalInfos.theme') }}</div>
            <strong>{{ info.theme }}</strong>
          </div>
        </div>
        <div v-if="info.planMediaCode && modulePlanMediaActive" class="column is-3">
          <div class="is-italic">{{ $t('detail.commande.planMedia') }}</div>
          <strong>
            <router-link-blank
              :message="info.planMediaCode"
              :to="{
                name: 'c.presse.plan_media',
                params: { id: info.planMediaId },
              }"
              class-message="lien">
            </router-link-blank>
          </strong>
        </div>
        <div v-if="modulePresseActive" class="column is-3">
          <div class="is-italic">{{ $t('detail.commande.regleFacturation.label') }}</div>
          <strong v-if="devisCommande.regleFacturationId">{{ getRegleFacturationLibelle }}</strong>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import { TYPE_COMMANDE } from '@/configuration/devisConf';
import { SOCIETE_ROLE_VENTE } from '@/configuration/societe/societe';
import { TYPE_REFERENCE_EXTERNE_ACHAT, TYPE_REFERENCE_EXTERNE_VENTE } from '@/configuration/dcf/dcfConf';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import { PARAMETRES_KEYS, PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';

const societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    info: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    devisCommande: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      constantes: {
        TYPE_REFERENCE_EXTERNE_VENTE,
        TYPE_REFERENCE_EXTERNE_ACHAT,
      },
    };
  },
  components: {
    routerLinkBlank,
  },
  computed: {
    ...societe.mapGetters({
      societesInformations: 'societesInformations',
      societeActive: 'societeActive',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    reglesFacturation() {
      return this.parametre(PARAMETRES_KEYS.regleFacturation);
    },
    modulePresseActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePresse.key);
    },
    moduleCampagneActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.campagne.key);
    },
    modulePlanMediaActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePlanMedia.key);
    },
    getTitleDateDevisSignature() {
      if (this.type === TYPE_COMMANDE) return this.$i18n.t('detail.generalInfos.dateSignature');
      return this.$i18n.t('detail.generalInfos.dateDevis');
    },
    labelEstSigne() {
      if (this.type === TYPE_COMMANDE) return this.$i18n.t('detail.generalInfos.labelEstSigneSurCommande');
      return this.$i18n.t('detail.generalInfos.estSigne');
    },
    getRole() {
      if (!this.info.role) return '';
      return SOCIETE_ROLE_VENTE.find((role) => {
        return role.code === this.info.role;
      }).libelle;
    },
    getRegleFacturationLibelle() {
      return this.reglesFacturation.find(r => r.id === this.devisCommande.regleFacturationId)?.libelle ?? '';
    },
    getAgenceLibelle() {
      const societeFind = this.societesInformations.find((s) => {
        return s.id === this.devisCommande.societeId;
      });
      if (!societeFind) return null;
      const agence = societeFind.agences.find((agence) => {
        return agence.id === this.info.agenceId;
      });
      if (agence) return agence.libelle;
      return null;
    },
    afficherCommission() {
      return this.type === TYPE_COMMANDE;
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametresInterne: 'loadParametresInterne',
    }),
  },
  async created() {
    try {
      await this.loadParametres([PARAMETRES_KEYS.regleFacturation]);
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.modulePlanMedia.key,
        PARAMETRES_INTERNE_KEYS.campagne.key]);
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    }
  },
};
</script>
